import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import AutocaptureConfig from './autocapture-config-ios.mdx';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`PostHog autocapture automatically tracks the following events for you:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Application Opened`}</strong>{` - when the app is opened from a closed state or when the app comes to the foreground (e.g. from the app switcher)`}</li>
      <li parentName="ul"><strong parentName="li">{`Application Backgrounded`}</strong>{` - when the app is sent to the background by the user`}</li>
      <li parentName="ul"><strong parentName="li">{`Application Installed`}</strong>{` - when the app is installed`}</li>
      <li parentName="ul"><strong parentName="li">{`Application Updated`}</strong>{` - when the app is updated`}</li>
      <li parentName="ul"><strong parentName="li">{`$screen`}</strong>{` - when the user navigates (if using `}<inlineCode parentName="li">{`UIViewController`}</inlineCode>{`)`}</li>
      <li parentName="ul"><strong parentName="li">{`$autocapture`}</strong>{` - when the user interacts with elements in a screen (if using `}<inlineCode parentName="li">{`UIKit`}</inlineCode>{`)`}</li>
    </ul>
    <blockquote>
      <p parentName="blockquote">{`🚧 `}<strong parentName="p">{`Note:`}</strong>{` `}<inlineCode parentName="p">{`$autocapture`}</inlineCode>{` is currently supported only in UIKit.`}</p>
    </blockquote>
    <h3 {...{
      "id": "capturing-screen-views"
    }}>{`Capturing screen views`}</h3>
    <p>{`With `}<a parentName="p" {...{
        "href": "/docs/libraries/ios/configuration#all-configuration-options"
      }}><inlineCode parentName="a">{`configuration.captureScreenViews`}</inlineCode></a>{` set as `}<inlineCode parentName="p">{`true`}</inlineCode>{`, PostHog will try to record all screen changes automatically.`}</p>
    <p>{`If you want to manually send a new screen capture event, use the `}<inlineCode parentName="p">{`screen`}</inlineCode>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`PostHogSDK.shared.screen("Dashboard", properties: ["fromIcon": "bottom"])
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Important:`}</strong>{` While `}<inlineCode parentName="p">{`captureScreenViews`}</inlineCode>{` works with both `}<inlineCode parentName="p">{`UIKit`}</inlineCode>{` and `}<inlineCode parentName="p">{`SwiftUI`}</inlineCode>{`, the screen names captured in `}<inlineCode parentName="p">{`SwiftUI`}</inlineCode>{` may not be very meaningful as they are based on internal SwiftUI view identifiers. For `}<inlineCode parentName="p">{`SwiftUI`}</inlineCode>{` applications, we recommend turning this option off and instead using the `}<inlineCode parentName="p">{`.postHogScreenView()`}</inlineCode>{` view modifier (see next section) to capture screen views with meaningful names.`}</p>
    </blockquote>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` You can use the `}<inlineCode parentName="p">{`BeforeSendBlock`}</inlineCode>{` to filter or drop any undesired screen events, giving you control over which screen views are sent to PostHog. See `}<a parentName="p" {...{
          "href": "/docs/libraries/ios#amending-dropping-or-sampling-events"
        }}>{`Amending, dropping or sampling events`}</a>{` for implementation examples.`}</p>
    </blockquote>
    <h3 {...{
      "id": "capturing-screen-views-in-swiftui"
    }}>{`Capturing screen views in SwiftUI`}</h3>
    <p>{`To track a screen view in `}<inlineCode parentName="p">{`SwiftUI`}</inlineCode>{`, apply the `}<inlineCode parentName="p">{`postHogScreenView`}</inlineCode>{` modifier to your full-screen views.
PostHog will send a `}<inlineCode parentName="p">{`$screen`}</inlineCode>{` event when the `}<inlineCode parentName="p">{`onAppear`}</inlineCode>{` action is executed and will infer a screen name based on the view’s type. You can provide a custom name and event properties if needed.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift",
        "metastring": "file=HomeView.swift",
        "file": "HomeView.swift"
      }}>{`// This will trigger a screen view event with $screen_name: "HomeViewContent"
struct HomeView: View {
    var body: some View {
        HomeViewContent()
            .postHogScreenView()
    }
}

// This will trigger a screen view event with $screen_name: "My Home View" and an additional event property from_button: "start"
struct HomeView: View {
    var body: some View {
        HomeViewContent()
            .postHogScreenView("My Home View", ["from_button": "start"])
    }
}
`}</code></pre>
    <p>{`In SwiftUI, views can range from entire screens to small UI components. Unlike UIKit, SwiftUI doesn’t clearly distinguish between these levels, which makes automatic tracking of full-screen views harder.`}</p>
    <h3 {...{
      "id": "adding-a-custom-label-on-autocaptured-elements"
    }}>{`Adding a custom label on autocaptured elements`}</h3>
    <p>{`PostHog automatically captures interactions with various UI elements in your app, but these interactions are often identified by element type names (e.g., UIButton, UITextField, UILabel). `}</p>
    <p>{`While this provides basic tracking, it can be challenging to pinpoint specific interactions with particular elements in your analytics. To make your data more meaningful and actionable, you can assign custom labels to any autocaptured element. These labels act as descriptive identifiers, making it easier to identify, filter, and analyze events in your reports.`}</p>
    <p><strong parentName="p">{`Adding a custom label in UIKit`}</strong></p>
    <p>{`To assign a custom label to a UIView, use the `}<inlineCode parentName="p">{`postHogLabel`}</inlineCode>{` property:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`let view = UIView()
view.postHogLabel = "usernameTextField"
`}</code></pre>
    <p>{`In this example, interactions with the UITextField will be captured with an additional identifier "usernameTextField".`}</p>
    <p><strong parentName="p">{`Adding a custom label in SwiftUI`}</strong></p>
    <p>{`In SwiftUI, use the `}<inlineCode parentName="p">{`.postHogLabel(_:)`}</inlineCode>{` modifier instead:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`var body: some View {
    ...
    TextField("username", text: $username)
        .postHogLabel("usernameTextField")
}
`}</code></pre>
    <p>{`In this example, interactions with the `}<em parentName="p">{`underlying`}</em>{` UITextField will be captured with an additional identifier "usernameTextField".`}</p>
    <p><strong parentName="p">{`Example of generated analytics data`}</strong></p>
    <p>{`The generated analytics element in the examples above will have the following form: `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`<UITextField id="usernameTextField">text value</UITextField>
`}</code></pre>
    <p><strong parentName="p">{`Filtering for labeled autocaptured elements in reports`}</strong></p>
    <p>{`To locate and filter interactions with specific elements in PostHog reports, you can use Autocapture element filters, such as:`}</p>
    <ul>
      <li parentName="ul">{`Tag Name (`}<inlineCode parentName="li">{`UITextField`}</inlineCode>{` in this example)`}</li>
      <li parentName="ul">{`Text (`}<inlineCode parentName="li">{`text value`}</inlineCode>{` in this example)`}</li>
      <li parentName="ul">{`CSS Selector (the generated `}<inlineCode parentName="li">{`id`}</inlineCode>{` attribute in this example)`}</li>
    </ul>
    <p>{`In the examples above, we can filter for the specific text field using the CSS Selector `}<inlineCode parentName="p">{`#usernameTextField`}</inlineCode></p>
    <h3 {...{
      "id": "interaction-autocapture"
    }}>{`Interaction autocapture`}</h3>
    <p>{`Interaction autocapture records when users interact with UI elements in your app. This includes:`}</p>
    <ul>
      <li parentName="ul">{`User interactions like `}<inlineCode parentName="li">{`touch`}</inlineCode>{`, `}<inlineCode parentName="li">{`swipe`}</inlineCode>{`, `}<inlineCode parentName="li">{`pan`}</inlineCode>{`, `}<inlineCode parentName="li">{`pinch`}</inlineCode>{`, `}<inlineCode parentName="li">{`rotation`}</inlineCode>{`, `}<inlineCode parentName="li">{`long_press`}</inlineCode>{`, `}<inlineCode parentName="li">{`scroll`}</inlineCode></li>
      <li parentName="ul">{`Control types `}<inlineCode parentName="li">{`value_changed`}</inlineCode>{`, `}<inlineCode parentName="li">{`submit`}</inlineCode>{`, `}<inlineCode parentName="li">{`toggle`}</inlineCode>{`, `}<inlineCode parentName="li">{`primary_action`}</inlineCode>{`, `}<inlineCode parentName="li">{`menu_action`}</inlineCode>{`, `}<inlineCode parentName="li">{`change`}</inlineCode></li>
    </ul>
    <p>{`Interaction autocapture is `}<strong parentName="p">{`not enabled by default`}</strong>{`. You can enable it by setting `}<inlineCode parentName="p">{`captureElementInteractions`}</inlineCode>{` to `}<inlineCode parentName="p">{`true`}</inlineCode>{` in the config.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift"
      }}>{`let config = PostHogConfig(apiKey: <ph_project_api_key>, host: <ph_client_api_host>)
config.captureElementInteractions = true // Disabled by default
PostHogSDK.shared.setup(config)
`}</code></pre>
    <h3 {...{
      "id": "autocapture-configuration"
    }}>{`Autocapture configuration`}</h3>
    <p>{`You can enable or disable autocapture through the `}<inlineCode parentName="p">{`PostHogConfig`}</inlineCode>{` object. Find more details about autocapture configuration in the `}<a parentName="p" {...{
        "href": "/docs/libraries/ios/configuration#autocapture-configuration"
      }}>{`configuration page`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      